import { AsyncLoadable } from '@v2/utils/mobx/async-loadable';
import { autorun, runInAction, toJS } from 'mobx';
import { RangeAddress } from 'shared/excel/range-address';
import { diContainer } from 'shared/infrastructure/di-container';
import { ProviderClient, ProviderFactory } from 'shared/providers/provider';
import { ProviderMeta } from 'shared/providers/provider-meta';
import { DataField, DataObject } from 'shared/providers/types';
import { HSObjectType } from 'shared/services/hubspot/types';
import { Flowstep, Operation, ProviderType } from 'shared/types/flowstep';
import { RangeType } from 'shared/utils/flowstep';
import { capitalizeString } from 'shared/utils/utils';
import office from 'src/services/office';
import { HasDisposer } from 'src/v2/states';
import { BaseEditorState } from '@v2/components/flowsteps/editors/common/states/base-editor-state';
import { ValidationErrorsMap } from 'src/v2/states/validation';
import { makeAutoObservableExt } from '@v2/utils/mobx/make-auto-observable-ext';

export type DeleteObjectsHSErrorKey =
  | 'target-range-not-selected'
  | 'object-type-not-selected'
  | 'id-column-not-selected';

export class DeleteObjectsHSState extends BaseEditorState implements HasDisposer {
  idColumn = '';
  onlyVisibleRows = true;
  showResultsTable = true;
  rangeToApply: { type: (typeof RangeType)[keyof typeof RangeType] } | null = {
    type: RangeType.Selected,
  };
  isValidRangeToApply = true;

  tableHeaders: string[] = [];
  target = '';
  worksheetId = '';
  targetRangeAddress: RangeAddress | null = null;
  isDetectingRange = false;

  objectTypes = new AsyncLoadable<DataObject[]>([]);
  objectFields = new AsyncLoadable<DataField[]>([]);
  private _objectTypeName: string | undefined = '';

  private provider: ProviderClient;
  private providerMeta: ProviderMeta;

  private disposers: (() => void)[] = [];

  constructor(private defaultRangeToApply?: (typeof RangeType)[keyof typeof RangeType]) {
    super({ operation: Operation.DELETE, provider: ProviderType.HS });
    makeAutoObservableExt(this, DeleteObjectsHSState);

    const providerFactory = diContainer.resolve<ProviderFactory>('providerFactory');
    this.provider = providerFactory.getProviderClient(ProviderType.HS);
    this.providerMeta = providerFactory.getProviderMeta(ProviderType.HS);

    this.objectTypes.load(async () => this.provider.loadObjectTypes());

    this.rangeToApply = { type: defaultRangeToApply || RangeType.Selected };

    this.disposers.push(
      autorun(() => {
        const objectType = this.selectedObjectType;

        this.objectFields.load(async () => {
          if (!objectType) return [];
          const hsType = objectType.originalType as HSObjectType;
          return await this.provider.loadObjectFields(hsType.id);
        });
      }),
    );
  }

  dispose() {
    this.disposers.forEach((dispose) => dispose());
  }

  override isSimilarFlowstep(flowstep: Flowstep): boolean {
    return flowstep.operation === Operation.DELETE && flowstep.provider === ProviderType.HS;
  }

  tryToLoadDifferentTypeFlowstep(sameTargetFlowsteps: Flowstep[], flowsepTarget: any) {
    this.setFlowstepTarget(flowsepTarget);
    const flowstep = sameTargetFlowsteps.find((x) => x.operation === Operation.PULL && x.provider === ProviderType.HS);
    if (!flowstep) {
      return;
    }
    this._objectTypeName = flowstep.providerOptions.objectType;
  }

  set objectTypeName(value: string | undefined) {
    this._objectTypeName = value;
  }

  get objectTypeName(): string | undefined {
    return this._objectTypeName;
  }

  get validationErrors() {
    const errors = new Map();

    this.validate(errors);
    this.validate(errors);

    return errors;
  }

  get isFlowstepButtonsDisabled(): boolean {
    return !this._objectTypeName || this.validationErrors.size > 0 || !this.isValidRangeToApply;
  }

  get selectedObjectType(): DataObject | undefined {
    const objectName = this._objectTypeName;
    return this.objectTypes.data.find((objectType) => objectType.id === objectName);
  }

  get generatedFlowstepName() {
    const sheetName = this.targetRangeAddress?.sheetName;
    return `Delete ${capitalizeString(this._objectTypeName)}` + (sheetName ? ` at '${sheetName}'` : '');
  }

  get loadingObjectTypesError() {
    return this.objectTypes.error;
  }

  get loadingObjectFieldsError() {
    return this.objectFields.error;
  }

  buildFlowstep(): Flowstep {
    return {
      id: this.flowstep?.id,
      flowId: this.flowstep?.flowId,
      operation: Operation.DELETE,
      name: this.name || this.generatedFlowstepName,
      errorReaction: this.errorReaction,
      provider: ProviderType.HS,
      version: '4',
      options: {
        ...super.buildFlowstep(),
        worksheetId: this.worksheetId,
        target: this.target,
        rangeToApply: toJS(this.rangeToApply),
        tableHeaders: toJS(this.tableHeaders),
        onlyVisibleRows: this.onlyVisibleRows,
        showOperationResultsTable: this.showResultsTable,
      },
      providerOptions: {
        objectType: this._objectTypeName,
        idColumn: this.idColumn,
      },
    };
  }

  onFlowstepLoaded(flowstep: Flowstep, meta: { isExact?: boolean; flowstepTarget?: any }): void {
    const { isExact, flowstepTarget } = meta;

    super.onFlowstepLoaded(flowstep, meta);

    const {
      worksheetId,
      target,
      rangeToApply,
      tableHeaders,
      onlyVisibleRows = true,
      showOperationResultsTable = true,
    } = flowstep.options;

    this.target = target;
    this.worksheetId = worksheetId;
    this.rangeToApply = this.defaultRangeToApply ? this.rangeToApply : rangeToApply;
    this.tableHeaders = tableHeaders ?? [];
    this.onlyVisibleRows = onlyVisibleRows;
    this.showResultsTable = showOperationResultsTable;

    const { objectType, idColumn } = flowstep.providerOptions ?? {};

    this.objectTypeName = objectType;
    this.idColumn = idColumn;

    if (flowstepTarget) {
      this.setFlowstepTarget(flowstepTarget);
      return;
    }

    if (isExact) {
      this.detectRange(true);
    }
  }

  validate(errors: ValidationErrorsMap<DeleteObjectsHSErrorKey>): void {
    this.validateTargetRange(errors);
    this.validateObjectType(errors);
    this.validateIdColumn(errors);
  }

  setFlowstepTarget(flowsepTarget: any) {
    runInAction(() => {
      this.targetRangeAddress = flowsepTarget.table ? RangeAddress.fromString(flowsepTarget.table.rangeAddress) : null;
      this.worksheetId = flowsepTarget.worksheetId || '';
      this.target = flowsepTarget ? flowsepTarget.targetCell : '';
      this.tableHeaders = flowsepTarget.table?.header.values || [];
      this.idColumn = this.tableHeaders.find((h) => h.toLowerCase() === this.providerMeta.getIdFieldlabel()) || '';
    });
  }

  async detectRange(useFlowstepTarget: boolean = false) {
    this.isDetectingRange = true;
    try {
      const result = (await office.getFlowstepTarget(
        useFlowstepTarget ? { worksheetId: this.worksheetId, targetCell: this.target } : undefined,
      )) as any;
      this.setFlowstepTarget(result);
    } finally {
      this.isDetectingRange = false;
    }
  }

  private validateTargetRange(errors: ValidationErrorsMap<DeleteObjectsHSErrorKey>) {
    if (!this.targetRangeAddress) {
      errors.set('target-range-not-selected', 'Target range must be selected');
    }
  }

  private validateObjectType(errors: ValidationErrorsMap<DeleteObjectsHSErrorKey>) {
    if (!this._objectTypeName) {
      errors.set('object-type-not-selected', 'Object type must be selected');
    }
  }

  private validateIdColumn(errors: ValidationErrorsMap<DeleteObjectsHSErrorKey>) {
    if (!this.idColumn) {
      errors.set('id-column-not-selected', 'ID column must be selected');
    }
  }
}
